/**
 * Applies the client's Brand Color from Portal Configuration to the portal's CSS variables.
 * styles/theme.css stays the fallback: an unset or invalid color clears any earlier override.
 */

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

/**
 * --brand is used as text on the page background (--bg in home.component.css), on white cards, and as a
 * background under white text. The page background is the darkest of those, so meeting the minimum there
 * meets it everywhere.
 */
const MIN_TEXT_CONTRAST = 4.5;
const PAGE_BACKGROUND = '#F7F8FA';
const DARKEN_STEP = 0.9;
const MAX_DARKEN_STEPS = 30;

/** Mix ratios chosen to approximate the template palette in theme.css from its primary (the dark shade comes out deeper). */
const DARK_MIX_TOWARD_BLACK = 0.4;
const LIGHT_MIX_TOWARD_WHITE = 0.93;
const ROW_HOVER_MIX_TOWARD_WHITE = 0.75;

type Rgb = [number, number, number];

export interface BrandPalette {
  brand: string;
  brandDark: string;
  brandLight: string;
  brandRowHover: string;
}

const PALETTE_VARIABLES: Record<keyof BrandPalette, string> = {
  brand: '--brand',
  brandDark: '--brand-dark',
  brandLight: '--brand-light',
  brandRowHover: '--brand-row-hover',
};

export function brandPalette(brandColor: unknown): BrandPalette | null {
  const base = parseHex(brandColor);
  if (!base) return null;
  const brand = readableOnPage(base);
  return {
    brand: toHex(brand),
    brandDark: toHex(mix(brand, BLACK, DARK_MIX_TOWARD_BLACK)),
    brandLight: toHex(mix(brand, WHITE, LIGHT_MIX_TOWARD_WHITE)),
    brandRowHover: toHex(mix(brand, WHITE, ROW_HOVER_MIX_TOWARD_WHITE)),
  };
}

/** Returns true when the palette was applied; otherwise removes any earlier override so theme.css applies again. */
export function applyBrandTheme(brandColor: unknown, root: HTMLElement = document.documentElement): boolean {
  const palette = brandPalette(brandColor);
  for (const key of Object.keys(PALETTE_VARIABLES) as (keyof BrandPalette)[]) {
    if (palette) {
      root.style.setProperty(PALETTE_VARIABLES[key], palette[key]);
    } else {
      root.style.removeProperty(PALETTE_VARIABLES[key]);
    }
  }
  return palette !== null;
}

export function contrastRatio(first: string, second: string): number {
  const a = parseHex(first);
  const b = parseHex(second);
  if (!a || !b) return 0;
  const lighter = Math.max(luminance(a), luminance(b));
  const darker = Math.min(luminance(a), luminance(b));
  return (lighter + 0.05) / (darker + 0.05);
}

function readableOnPage(color: Rgb): Rgb {
  let candidate = color;
  for (let step = 0; step < MAX_DARKEN_STEPS && contrastRatio(toHex(candidate), PAGE_BACKGROUND) < MIN_TEXT_CONTRAST; step++) {
    candidate = candidate.map((channel) => Math.floor(channel * DARKEN_STEP)) as Rgb;
  }
  return candidate;
}

function parseHex(value: unknown): Rgb | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!HEX_COLOR.test(text)) return null;
  const hex = text.length === 4 ? [...text.slice(1)].map((c) => c + c).join('') : text.slice(1);
  return [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16)) as Rgb;
}

function mix(color: Rgb, target: Rgb, amount: number): Rgb {
  return color.map((channel, index) => Math.round(channel + (target[index] - channel) * amount)) as Rgb;
}

function luminance([red, green, blue]: Rgb): number {
  const linear = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linear(red) + 0.7152 * linear(green) + 0.0722 * linear(blue);
}

function toHex(color: Rgb): string {
  return `#${color.map((channel) => (channel < 16 ? '0' : '') + channel.toString(16)).join('').toUpperCase()}`;
}
